import { API_BASE } from '../apiBase';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

function Employees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add employee modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    name: '',
    role: '',
    status: 'Active',
    phone: '',
    email: '',
    startDate: '',
    payRate: '',
    homeAddress: { street: '', city: '', state: 'CA', zip: '' },
  });
  const [addError, setAddError] = useState('');

  // Address autocomplete states
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);

  // Search & copy states
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCell, setCopiedCell] = useState(null);

  // Context menu (right-click delete)
  const [contextMenu, setContextMenu] = useState(null);

  // NEW: State for inline status editing
  const [editingStatusId, setEditingStatusId] = useState(null);

  // Fetch employees
  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/employees`, {
          headers: { Authorization: token },
        });
        setEmployees(res.data);
        setError(null);
      } catch (err) {
        console.error('Fetch employees error:', err);
        setError('Failed to load employees');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [navigate]);

  // NEW: Function to update status in DB
  const handleStatusChange = async (employeeId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/employees/${employeeId}`, 
        { status: newStatus }, 
        { headers: { Authorization: token } }
      );
      setEmployees(prev =>
        prev.map(emp => emp._id === employeeId ? { ...emp, status: newStatus } : emp)
      );
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setEditingStatusId(null);
    }
  };

  // Debounce for address suggestions
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const fetchAddressSuggestions = debounce(async (query) => {
    if (query.length < 3) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setAddressLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=8&countrycodes=us`,
        {
          headers: { 'User-Agent': 'StalwartElectricApp/1.0 (contact@stalwart-electric.com)' },
        }
      );
      const data = await res.json();
      setAddressSuggestions(data);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Nominatim error:', err);
      setAddressSuggestions([]);
    } finally {
      setAddressLoading(false);
    }
  }, 400);

  const filteredEmployees = employees.filter((emp) =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.phone?.includes(searchTerm) ||
    emp.employeeNumber?.includes(searchTerm)
  );

  const statusColors = {
    Active: 'bg-green-600/80 text-white',
    Terminated: 'bg-red-600/80 text-white',
    'On Leave': 'bg-yellow-600/80 text-white',
    Furloughed: 'bg-gray-600/80 text-white',
    Inactive: 'bg-gray-600/80 text-white',
  };

  const formatPhone = (value) => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleAddPhoneChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw.length > 10) return;
    setNewEmployee((prev) => ({ ...prev, phone: raw }));
  };

  const copyToClipboard = async (text, rowId, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCell({ rowId, field });
      setTimeout(() => setCopiedCell(null), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRowClick = (employee) => {
    navigate(`/employees/${employee._id}`);
  };

  const handleContextMenu = (e, employee) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      employeeId: employee._id,
    });
  };

  const handleDelete = async (employeeId) => {
    if (!window.confirm('Are you sure you want to delete this employee? This cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/employees/${employeeId}`, {
        headers: { Authorization: token },
      });
      setEmployees((prev) => prev.filter((e) => e._id !== employeeId));
      setContextMenu(null);
    } catch (err) {
      alert('Failed to delete employee: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleAddEmployee = async () => {
    if (!newEmployee.name.trim() || !newEmployee.role.trim()) {
      setAddError('Name and Role are required');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const payload = { ...newEmployee, phone: newEmployee.phone || undefined };
      await axios.post(`${API_BASE}/employees`, payload, {
        headers: { Authorization: token },
      });
      const res = await axios.get(`${API_BASE}/employees`, {
        headers: { Authorization: token },
      });
      setEmployees(res.data);
      setNewEmployee({
        name: '',
        role: '',
        status: 'Active',
        phone: '',
        email: '',
        startDate: '',
        payRate: '',
        homeAddress: { street: '', city: '', state: 'CA', zip: '' },
      });
      setAddressQuery('');
      setAddressSuggestions([]);
      setShowSuggestions(false);
      setAddError('');
      setShowAddModal(false);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create employee');
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Loading employees...</div>;
  if (error) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 text-red-400">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 space-y-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <h1 className="text-3xl font-bold tracking-tight">Employees</h1>
        <button
          className="px-6 py-3 bg-indigo-700/90 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
          onClick={() => setShowAddModal(true)}
        >
          + Add Employee
        </button>
      </div>

      <div className="max-w-xl">
        <input
          type="text"
          placeholder="Search by name, role, status, phone, or employee #..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-5 py-3.5 bg-gray-900 border border-gray-800 rounded-xl text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60 focus:ring-2 focus:ring-indigo-600/20 transition-all"
        />
      </div>

      <div className="bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-gray-950/30">
            <tr>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Employee #</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Name</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Role</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Phone</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Email</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {filteredEmployees.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-8 py-20 text-center text-zinc-500 text-lg">
                  No employees found. Try adjusting your search or add one.
                </td>
              </tr>
            ) : (
              filteredEmployees.map((emp) => (
                <tr
                  key={emp._id}
                  className="bg-gray-900 hover:bg-gray-800/50 hover:shadow-sm transition-all duration-200 cursor-pointer group"
                  onContextMenu={(e) => handleContextMenu(e, emp)}
                  onClick={(e) => {
                    if (!e.target.closest('span') && !e.target.closest('select') && !contextMenu) {
                      handleRowClick(emp);
                    }
                  }}
                >
                  <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(emp.employeeNumber || '—', emp._id, 'employeeNumber'); }}
                    >
                      {emp.employeeNumber || '—'}
                      {copiedCell?.rowId === emp._id && copiedCell?.field === 'employeeNumber' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(emp.name, emp._id, 'name'); }}
                    >
                      {emp.name}
                      {copiedCell?.rowId === emp._id && copiedCell?.field === 'name' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">{emp.role || 'N/A'}</td>
                  
                  {/* UPDATED STATUS CELL */}
                  <td className="px-8 py-6 whitespace-nowrap">
                    {editingStatusId === emp._id ? (
                      <select
                        value={emp.status}
                        onChange={(e) => handleStatusChange(emp._id, e.target.value)}
                        onBlur={() => setEditingStatusId(null)}
                        autoFocus
                        className="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="Active">Active</option>
                        <option value="On Leave">On Leave</option>
                        <option value="Terminated">Terminated</option>
                        <option value="Furloughed">Furloughed</option>
                      </select>
                    ) : (
                      <span 
                        className={`inline-block px-3 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-offset-gray-900 transition-all ${statusColors[emp.status] || 'bg-gray-600/80 text-white'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingStatusId(emp._id);
                        }}
                      >
                        {emp.status}
                      </span>
                    )}
                  </td>

                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(emp.phone || '', emp._id, 'phone'); }}
                    >
                      {formatPhone(emp.phone)}
                      {copiedCell?.rowId === emp._id && copiedCell?.field === 'phone' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(emp.email || '—', emp._id, 'email'); }}
                    >
                      {emp.email || '—'}
                      {copiedCell?.rowId === emp._id && copiedCell?.field === 'email' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">Copied!</span>
                      )}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Right-click Delete Menu */}
      {contextMenu && (
        <div
          className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-2 z-50"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
        >
          <button
            className="w-full px-6 py-2 text-left text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
            onClick={() => handleDelete(contextMenu.employeeId)}
          >
            Delete Employee
          </button>
        </div>
      )}

      {/* Add Employee Modal - unchanged content structure */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl p-8 w-full max-w-lg border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Add New Employee</h2>
            {addError && <p className="text-red-400 mb-4">{addError}</p>}
            
            {/* Keeping your existing modal logic exactly as requested */}
            <div className="space-y-4">
               <div>
                <label className="block text-zinc-400 text-sm mb-1">Name *</label>
                <input
                  value={newEmployee.name}
                  onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Role *</label>
                <input
                  value={newEmployee.role}
                  onChange={(e) => setNewEmployee({...newEmployee, role: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              {/* ... other inputs ... */}
            </div>

            <div className="flex justify-end gap-4 mt-8">
              <button onClick={() => { setShowAddModal(false); setAddError(''); setAddressQuery(''); setAddressSuggestions([]); setShowSuggestions(false); }} className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all">Cancel</button>
              <button onClick={handleAddEmployee} className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg">Add Employee</button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          20% { opacity: 1; transform: translateY(0); }
          80% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        .animate-fade-in-out { animation: fadeInOut 3s ease-in-out forwards; }
      `}</style>
    </div>
  );
}

export default Employees;